import api from '@/services/index';

export default {
  namespaced: true,
  state: {
    session: null,
    lastAnswerResult: null,
    training: null,
    themesToStudy: [],
    isLoading: {
      session: false,
      answer: false,
      training: false,
      themesToStudy: false,
    },
    errors: {
      createSession: {},
      loadSession: {},
      submitAnswer: {},
      startTraining: {},
    },
  },
  getters: {
    session: state => state.session,
    currentQuestion: state => (state.session ? state.session.current_question : null),
    lastAnswerResult: state => state.lastAnswerResult,
    isLoadingSession: state => state.isLoading.session,
    isLoadingAnswer: state => state.isLoading.answer,
    training: state => state.training,
    themesToStudy: state => state.themesToStudy,
    isLoadingThemes: state => state.isLoading.themesToStudy,
    trainingCurrentQuestion: (state) => {
      if (!state.training || !state.training.questions) return null;
      return state.training.questions[state.training.pos] || null;
    },
    trainingIsFinished: (state) => {
      if (!state.training || !state.training.questions) return false;
      return state.training.pos >= state.training.questions.length;
    },
    isLoadingTraining: state => state.isLoading.training,
    isCompleted: (state) => {
      if (!state.session || !state.session.state) return false;
      return state.session.state.phase === 'completed';
    },
    progress: (state) => {
      if (!state.session || !state.session.state) {
        return {
          mastered: 0,
          total: 0,
          answered: 0,
          phase: null,
          seenUnique: 0,
          correctLast: 0,
          wrongLast: 0,
        };
      }
      const s = state.session.state;
      return {
        mastered: s.mastered_count || 0,
        total: s.total_questions || 0,
        answered: s.total_answered || 0,
        phase: s.phase || null,
        seenUnique: s.seen_unique || 0,
        correctLast: s.correct_last || 0,
        wrongLast: s.wrong_last || 0,
      };
    },
  },
  mutations: {
    SET_SESSION(state, payload) {
      state.session = payload;
    },
    SET_CURRENT_QUESTION(state, question) {
      if (state.session) {
        state.session = { ...state.session, current_question: question };
      }
    },
    SET_SESSION_STATE(state, sessionState) {
      if (state.session) {
        state.session = { ...state.session, state: sessionState };
      }
    },
    SET_LAST_ANSWER_RESULT(state, payload) {
      state.lastAnswerResult = payload;
    },
    CLEAR_LAST_ANSWER_RESULT(state) {
      state.lastAnswerResult = null;
    },
    SET_LOADING(state, { key, value }) {
      state.isLoading[key] = value;
    },
    SET_ERROR(state, { key, value }) {
      state.errors[key] = value;
    },
    SET_TRAINING(state, payload) {
      state.training = payload;
    },
    ADVANCE_TRAINING_POSITION(state) {
      if (state.training) {
        state.training = { ...state.training, pos: state.training.pos + 1 };
      }
    },
    CLEAR_TRAINING(state) {
      state.training = null;
    },
    SET_THEMES_TO_STUDY(state, payload) {
      state.themesToStudy = payload;
    },
  },
  actions: {
    async createSession({ commit }, payload) {
      commit('SET_ERROR', { key: 'createSession', value: {} });
      commit('SET_LOADING', { key: 'session', value: true });
      try {
        const resp = await api.post('/marathon/sessions', payload);
        commit('SET_SESSION', resp.data);
        commit('CLEAR_LAST_ANSWER_RESULT');
        return resp.data;
      } catch (e) {
        const data = (e.response && e.response.data) || { error: e.message || 'Ошибка' };
        commit('SET_ERROR', { key: 'createSession', value: data });
        throw e;
      } finally {
        commit('SET_LOADING', { key: 'session', value: false });
      }
    },

    async loadSession({ commit }, { uuid }) {
      commit('SET_ERROR', { key: 'loadSession', value: {} });
      commit('SET_LOADING', { key: 'session', value: true });
      try {
        const resp = await api.get(`/marathon/sessions/${uuid}`);
        commit('SET_SESSION', resp.data);
        commit('CLEAR_LAST_ANSWER_RESULT');
        return resp.data;
      } catch (e) {
        const data = (e.response && e.response.data) || { error: e.message || 'Ошибка' };
        commit('SET_ERROR', { key: 'loadSession', value: data });
        throw e;
      } finally {
        commit('SET_LOADING', { key: 'session', value: false });
      }
    },

    async submitAnswer({ commit }, {
      uuid, questionId, answer, timeMs,
    }) {
      commit('SET_ERROR', { key: 'submitAnswer', value: {} });
      commit('SET_LOADING', { key: 'answer', value: true });
      try {
        const body = { question_id: questionId, answer };
        if (typeof timeMs === 'number') body.time_ms = timeMs;

        const resp = await api.post(`/marathon/sessions/${uuid}/answer`, body);
        commit('SET_LAST_ANSWER_RESULT', {
          is_correct: resp.data.is_correct,
          correct_answer: resp.data.correct_answer,
          comment: resp.data.comment,
          question_stat: resp.data.question_stat,
          answer,
        });
        commit('SET_SESSION_STATE', resp.data.state);
        return resp.data;
      } catch (e) {
        const data = (e.response && e.response.data) || { error: e.message || 'Ошибка' };
        commit('SET_ERROR', { key: 'submitAnswer', value: data });
        throw e;
      } finally {
        commit('SET_LOADING', { key: 'answer', value: false });
      }
    },

    async skipQuestion({ commit }, { uuid, questionId }) {
      commit('SET_LOADING', { key: 'answer', value: true });
      try {
        const resp = await api.post(`/marathon/sessions/${uuid}/skip`, { question_id: questionId });
        commit('SET_SESSION_STATE', resp.data.state);
        return resp.data;
      } finally {
        commit('SET_LOADING', { key: 'answer', value: false });
      }
    },

    advanceToNextQuestion({ commit }, nextQuestion) {
      commit('SET_CURRENT_QUESTION', nextQuestion || null);
      commit('CLEAR_LAST_ANSWER_RESULT');
    },

    clearSession({ commit }) {
      commit('SET_SESSION', null);
      commit('CLEAR_LAST_ANSWER_RESULT');
    },

    async startTraining({ commit }, { uuid, limit, onlyUnmastered }) {
      commit('SET_ERROR', { key: 'startTraining', value: {} });
      commit('SET_LOADING', { key: 'training', value: true });
      try {
        const body = {};
        if (limit !== null && limit !== undefined && limit !== 'all') {
          body.limit = parseInt(limit, 10);
        }
        body.only_unmastered = onlyUnmastered !== false;
        const resp = await api.post(`/marathon/sessions/${uuid}/training`, body);
        commit('SET_TRAINING', { questions: resp.data.questions, pos: 0, categoryName: resp.data.category_name });
        return resp.data;
      } catch (e) {
        const data = (e.response && e.response.data) || { error: e.message || 'Ошибка' };
        commit('SET_ERROR', { key: 'startTraining', value: data });
        throw e;
      } finally {
        commit('SET_LOADING', { key: 'training', value: false });
      }
    },

    advanceTrainingQuestion({ commit }) {
      commit('ADVANCE_TRAINING_POSITION');
      commit('CLEAR_LAST_ANSWER_RESULT');
    },

    clearTraining({ commit }) {
      commit('CLEAR_TRAINING');
      commit('CLEAR_LAST_ANSWER_RESULT');
    },

    async deleteSession({ commit }, { uuid }) {
      commit('SET_LOADING', { key: 'session', value: true });
      try {
        await api.delete(`/marathon/sessions/${uuid}`);
        commit('SET_SESSION', null);
        commit('CLEAR_LAST_ANSWER_RESULT');
      } finally {
        commit('SET_LOADING', { key: 'session', value: false });
      }
    },

    async loadThemesToStudy({ commit }, { uuid, onlyUnsolved = false }) {
      commit('SET_LOADING', { key: 'themesToStudy', value: true });
      try {
        const params = onlyUnsolved ? { only_unsolved: 1 } : {};
        const resp = await api.get(`/marathon/sessions/${uuid}/themes-to-study`, { params });
        commit('SET_THEMES_TO_STUDY', resp.data);
      } catch (e) {
        commit('SET_THEMES_TO_STUDY', []);
      } finally {
        commit('SET_LOADING', { key: 'themesToStudy', value: false });
      }
    },
  },
};
